
import { Component, Ref, Vue } from 'vue-property-decorator'
import CompareLearningProgress from '@/components/templates/manage/CompareLearningProgress.vue'
import { LearningProgressList } from '@/models/manage/LearningProgressList'
import { SelectItem } from '@/models/SelectItem'
import { GradeCode } from '@/const/GradeCode'
import { SubjectCode } from '@/const/SubjectCode'
import { StudyCategoriesApi } from '@/api/methods/StudyCategoriesApi'
import { AcademyCoursesApi } from '@/api/methods/manage/AcademyCoursesApi'
import { SummaryStudyCategoryProgressClassesApi } from '@/api/methods/manage/SummaryStudyCategoryProgressClassesApi'
import StringUtils from '@/utils/StringUtils'

/**
 * クラス別通常学習進捗比較
 */
@Component({
  components: {
    CompareLearningProgress,
  },
})
export default class ClassLearningProgress extends Vue {
  /** 通常学習進捗比較レイアウト */
  @Ref()
  compareLearningProgress!: CompareLearningProgress

  /** 教材フィルターの選択肢がない場合の固定値 */
  private readonly OPTION_DISABLED_STUDY_CATEGORY = { value: '', label: '教材がありません' }

  /** コースフィルターの選択肢がない場合の固定値 */
  private readonly OPTION_DISABLED_COURSE = { value: '', label: 'コースがありません' }

  /** 学年フィルターの選択肢 */
  private filterGradeList: SelectItem[] = []

  /** 教科フィルターの選択肢 */
  private filterSubjectList: SelectItem[] = []

  /** 教材フィルターの選択肢 */
  private filterStudyCategoryList: SelectItem[] = []

  /** 教材フィルターDisabled制御 */
  private isDisabledStudyCategoryFilter = false

  /** コースフィルターの選択肢 */
  private filterCourseList: SelectItem[] = []

  /** コースフィルターDisabled制御 */
  private isDisabledCourseFilter = false

  /** 通常学習進捗集計データ一覧 */
  private summaryProgressList: LearningProgressList.Table[] = []

  /** 先生情報 */
  private teacher = this.$appCookies.authTeacher.get()

  /**
   * 学年フィルターの初期選択値
   *
   * @returns 学年コード
   */
  private get defaultGradeCode(): string {
    let gradeCode = this.$route.query.gradeCode

    if (!gradeCode || typeof gradeCode !== 'string') {
      // 未指定時、指定不正時は空文字列に置換
      gradeCode = ''
    }

    // 定義情報から有効な学年情報を取得
    const grade = GradeCode.findByCode(gradeCode)

    return grade?.code || GradeCode.LIST[0].code
  }

  /**
   * 教科フィルターの初期選択値
   *
   * @returns 教科コード
   */
  private get defaultSubjectCode(): string {
    let subjectCode = this.$route.query.subjectCode

    if (!subjectCode || typeof subjectCode !== 'string') {
      // 未指定時、指定不正時は空文字列に置換
      subjectCode = ''
    }

    // 定義情報から有効な学年情報を取得
    const subject = SubjectCode.findByCode(subjectCode)

    return subject?.code || SubjectCode.LIST[0].code
  }

  /**
   * クエリパラメータから取得した教材ID
   *
   * @returns 教材ID
   */
  private get queryStudyCategoryId(): string | undefined {
    const studyCategoryId = this.$route.query.studyCategoryId

    if (!studyCategoryId || typeof studyCategoryId !== 'string') {
      // 未指定時、string型でない場合はundefinedを返す
      return undefined
    }

    return studyCategoryId
  }

  /**
   * 選択中の学年
   *
   * @returns 学年コード
   */
  private get currentGrade(): string {
    return this.compareLearningProgress.currentGrade()
  }

  /**
   * 選択中の教科
   *
   * @returns 教科コード
   */
  private get currentSubject(): string {
    return this.compareLearningProgress.currentSubject()
  }

  /**
   * 選択中の教材
   *
   * @returns 教材コード(ID)
   */
  private get currentStudyCategory(): string | undefined {
    // 教材が未指定の場合、undefindを返す
    if (StringUtils.isBlank(this.compareLearningProgress.currentStudyCategory())) {
      return undefined
    }

    return this.compareLearningProgress.currentStudyCategory()
  }

  /**
   * 選択中のコースを取得
   *
   * @returns コースコード(ID)
   */
  private get currentCourse(): string | undefined {
    // コースが未指定だった場合、undefinedを返す
    if (StringUtils.isBlank(this.compareLearningProgress.currentCourse())) {
      return undefined
    }

    return this.compareLearningProgress.currentCourse()
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 学年一覧取得(フィルター設定)
    this.getGradeList()

    // 教科一覧取得(フィルター設定)
    this.getSubjectList()

    // 教材一覧取得(フィルター設定)
    await this.getStudyCategoryList(this.queryStudyCategoryId)

    // コース一覧取得(フィルター設定)
    await this.getCourseList()

    // 進捗集計データ取得
    await this.getSummaryProgressList()

    // クエリが存在しない場合、クエリパラメータを更新
    if (Object.keys(this.$route.query).length === 0) {
      await this.replaceQueryParams()
    }

    this.$loading.complete()
  }

  /**
   * 学年一覧取得(フィルター設定)
   */
  private getGradeList(): void {
    const gradeList = GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })

    this.filterGradeList = gradeList
  }

  /**
   * 教科一覧取得(フィルター設定)
   */
  private getSubjectList(): void {
    const subjectList = SubjectCode.LIST.map((subject) => {
      return {
        value: subject.code,
        label: subject.label,
      }
    })

    this.filterSubjectList = subjectList
  }

  /**
   * 教材一覧取得(フィルター設定)
   *
   * @param studyCategoryId 教材ID
   */
  private async getStudyCategoryList(studyCategoryId?: string): Promise<void> {
    // Disabledを解除
    this.isDisabledStudyCategoryFilter = false

    // 教材一覧取得
    const studyCategories = await StudyCategoriesApi.getGenerals(this.currentGrade, this.currentSubject)

    // 教材がない場合、固定値を設定し処理を終了
    if (studyCategories.length === 0) {
      this.filterStudyCategoryList = [this.OPTION_DISABLED_STUDY_CATEGORY]
      this.setCurrentStudyCategory(this.OPTION_DISABLED_STUDY_CATEGORY.value)
      this.isDisabledStudyCategoryFilter = true
      return
    }

    // 教材フィルターセット
    this.filterStudyCategoryList = studyCategories.map((studyCategory) => {
      return {
        // 教材IDをコードとして使用
        value: String(studyCategory.id),
        label: studyCategory.name,
      }
    })

    // 初期選択値設定
    this.setCurrentStudyCategory(studyCategoryId || this.filterStudyCategoryList[0].value)
  }

  /**
   * 教材フィルターの選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  private setCurrentStudyCategory(value: string): void {
    // 指定教材の存在チェック
    const studyCategory = this.filterStudyCategoryList.find((studyCategory) => studyCategory.value === value)

    this.compareLearningProgress.setCurrentStudyCategory(studyCategory?.value || this.filterStudyCategoryList[0].value)
  }

  /**
   * コース一覧取得
   */
  private async getCourseList(): Promise<void> {
    // Disabledを解除
    this.isDisabledCourseFilter = false

    // パラメータ定義
    let options = {
      branchId: this.teacher.branchId,
    }

    // 学年指定があれば追加
    if (this.currentGrade) {
      Object.assign(options, { gradeCode: this.currentGrade })
    }

    // コース一覧取得
    const courses = await AcademyCoursesApi.getCoursesHasStudents(options)

    // コースがない場合、固定値を設定し処理を終了
    if (courses.length === 0) {
      this.filterCourseList = [this.OPTION_DISABLED_COURSE]
      this.setCurrentCourse(this.OPTION_DISABLED_COURSE.value)
      this.isDisabledCourseFilter = true
      return
    }

    // コースフィルター設定
    this.filterCourseList = courses.map((course) => {
      return {
        // コースIDをコードとして使用
        value: String(course.id),
        label: course.name,
      }
    })

    // 初期選択値設定
    this.setCurrentCourse(this.filterCourseList[0].value)
  }

  /**
   * コースフィルターの選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  private setCurrentCourse(value: string): void {
    // 指定コースの存在チェック
    const course = this.filterCourseList.find((course) => course.value === value)

    this.compareLearningProgress.setCurrentCourse(course?.value || this.filterCourseList[0].value)
  }

  /**
   * 通常学習進捗集計データ一覧を取得
   */
  private async getSummaryProgressList(): Promise<void> {
    // 選択中の教材または選択中のコースが取得できない場合、通常学習進捗集計データ一覧クリアし処理を終了
    if (!this.currentStudyCategory || !this.currentCourse) {
      this.summaryProgressList = []
      return
    }

    // 通常学習進捗集計データ取得
    const summary = await SummaryStudyCategoryProgressClassesApi.getSummary(
      this.teacher.branchId,
      Number(this.currentCourse),
      Number(this.currentStudyCategory)
    )

    // クラス毎のデータが存在しない場合終了
    if (summary.branchClasses.length === 0) {
      this.summaryProgressList = []
      return
    }

    // テーブル表示用データ設定
    this.summaryProgressList = summary.branchClasses.map((academyClass) => {
      return {
        value: Math.round(academyClass.average),
        label: `${academyClass.className}平均`,
        color: '--color-learning-progress-chart__class',
      }
    })

    // テーブル用データの先頭に全クラス平均を追加
    this.summaryProgressList.unshift({
      value: Math.round(summary.totalAverage),
      label: '全クラス平均',
      color: '--color-learning-progress-chart__class__total',
    })
  }

  /**
   * コースタブクリック時の処理
   */
  private clickCourseTab(): void {
    // コース別進捗比較画面に遷移
    this.$router.push({
      path: '/teacher/course/progress',
      query: {
        subjectCode: this.currentSubject,
        gradeCode: this.currentGrade,
        studyCategoryId: this.currentStudyCategory,
      },
    })
  }

  /**
   * クエリパラメータの更新
   */
  private async replaceQueryParams(): Promise<void> {
    await this.$router.replace({
      query: {
        subjectCode: this.currentSubject,
        gradeCode: this.currentGrade,
        studyCategoryId: this.currentStudyCategory,
      },
    })
  }

  /**
   * 学年フィルター変更時の処理
   */
  private async changeGradeFilter(): Promise<void> {
    this.$loading.start()

    // 教材一覧取得(フィルター設定)
    await this.getStudyCategoryList()

    // コース一覧取得(フィルター設定)
    await this.getCourseList()

    // 進捗集計データ取得
    await this.getSummaryProgressList()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * 教科フィルター変更時の処理
   */
  private async changeSubjectFilter(): Promise<void> {
    this.$loading.start()

    // 教材一覧取得(フィルター設定)
    await this.getStudyCategoryList()

    // 進捗集計データ取得
    await this.getSummaryProgressList()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * 教材フィルター変更時の処理
   */
  private async changeStudyCategoryFilter(): Promise<void> {
    this.$loading.start()

    // 進捗集計データ取得
    await this.getSummaryProgressList()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * コースフィルター変更時の処理
   */
  private async changeCourseFilter(): Promise<void> {
    this.$loading.start()

    // 進捗集計データ取得
    await this.getSummaryProgressList()

    this.$loading.complete()
  }

  /**
   * ダウンロードボタン押下時の処理
   *
   * TODO: ダウンロード機能実装後に修正
   */
  private downloadLearningProgress(): void {
    alert('ダウンロード')
  }
}
